import React from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { ScheduleRecord } from '~/services/OffersService';

import {
  AppExclusiveStatus,
  formatSlotTime,
  formatSlotTimeOfDay,
  getOfferLocationLabel,
  getScheduleStatus,
} from './appOnlyFlashCalendar';

const STATUS_LABELS: Record<AppExclusiveStatus, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  ended: 'Ended',
};

interface Props {
  schedule: ScheduleRecord;
  offer: App.Offer | undefined;
  isOfferLoading: boolean;
}

export default function AppOnlyFlashEventCard({ schedule, offer, isOfferLoading }: Props) {
  const theme = useTheme();
  const status = getScheduleStatus(schedule);
  const accent = {
    scheduled: theme.palette.info.main,
    live: theme.palette.success.main,
    ended: theme.palette.grey[500],
  }[status];
  const offerName = offer?.name ?? schedule.fk_offer_salesforce_id;
  const locationLabel = getOfferLocationLabel(offer?.location);

  return (
    <Tooltip
      title={`${offerName} · ${formatSlotTime(schedule.start)} – ${formatSlotTime(schedule.end)} (Sydney) · ${
        STATUS_LABELS[status]
      }`}
    >
      <Box
        component={RouterLink}
        to={`/offers/${schedule.fk_offer_salesforce_id}`}
        aria-label={`${offerName}, ${STATUS_LABELS[status]}`}
        sx={{
          display: 'block',
          mb: 0.5,
          px: 0.75,
          py: 0.75,
          borderRadius: 0.75,
          border: 1,
          borderColor: alpha(accent, 0.25),
          borderLeft: 3,
          borderLeftColor: accent,
          bgcolor: alpha(accent, status === 'ended' ? 0.06 : 0.1),
          color: 'text.primary',
          textDecoration: 'none',
          '&:hover': { boxShadow: 1 },
        }}
      >
        {isOfferLoading && !offer ? (
          <Skeleton width="80%" />
        ) : (
          <Typography variant="caption" component="p" fontWeight={500} noWrap>
            {offerName}
          </Typography>
        )}
        {locationLabel && (
          <Typography variant="caption" component="p" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
            {locationLabel}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between" mt={0.5}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {formatSlotTimeOfDay(schedule.start)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10, color: accent }}>
            {STATUS_LABELS[status]}
          </Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
}
